import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Mail, MapPin, Phone } from "lucide-react";
import { MaskedReveal } from "./MaskedReveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  "Managed IT",
  "Cybersecurity",
  "Cloud & Infrastructure",
  "24/7 Helpdesk",
  "Backup & Disaster Recovery",
  "Not sure yet",
];

const inputClass =
  "w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-[#5A6472] focus:border-[#00E5FF] focus:outline-none transition-colors duration-300";

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message || "Message sent. We'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#0A0A0C] border-t border-white/10 py-24 md:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
        {/* Left: intro + contact info */}
        <div className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.28em] text-[#00E5FF] font-bold mb-6">
            Start a project
          </p>
          <MaskedReveal
            as="h2"
            testId="contact-heading"
            lines={["Let's make your", "IT invisible."]}
            className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[0.95]"
          />
          <p className="mt-8 text-[#A0AEC0] leading-relaxed max-w-md">
            Tell us where it hurts. We&apos;ll respond within one business day
            with a clear, no-pressure plan for your infrastructure.
          </p>

          <div className="mt-12 space-y-5">
            <a
              href="mailto:jleonard@leonardtechinc.com"
              data-testid="contact-email-link"
              className="flex items-center gap-4 text-white hover:text-[#00E5FF] transition-colors duration-300"
            >
              <span className="h-10 w-10 rounded-full border border-white/10 bg-white/5 flex items-center justify-center text-[#00E5FF]">
                <Mail size={18} />
              </span>
              jleonard@leonardtechinc.com
            </a>
            <div className="flex items-center gap-4 text-[#A0AEC0]">
              <span className="h-10 w-10 rounded-full border border-white/10 bg-white/5 flex items-center justify-center text-[#00E5FF]">
                <Phone size={18} />
              </span>
              +1 (631) 836-9593
            </div>
            <div className="flex items-center gap-4 text-[#A0AEC0]">
              <span className="h-10 w-10 rounded-full border border-white/10 bg-white/5 flex items-center justify-center text-[#00E5FF]">
                <MapPin size={18} />
              </span>
              Serving businesses nationwide
            </div>
          </div>
        </div>

        {/* Right: form */}
        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="lg:col-span-7 rounded-2xl border border-white/10 bg-[#050505] p-8 md:p-12"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#5A6472] mb-2 block">
                Full name *
              </label>
              <input
                data-testid="contact-name"
                className={inputClass}
                placeholder="Jane Cooper"
                value={form.name}
                onChange={update("name")}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#5A6472] mb-2 block">
                Work email *
              </label>
              <input
                data-testid="contact-email"
                type="email"
                className={inputClass}
                placeholder="jane@company.com"
                value={form.email}
                onChange={update("email")}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#5A6472] mb-2 block">
                Company
              </label>
              <input
                data-testid="contact-company"
                className={inputClass}
                placeholder="Company Inc."
                value={form.company}
                onChange={update("company")}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#5A6472] mb-2 block">
                Service of interest
              </label>
              <select
                data-testid="contact-service"
                className={`${inputClass} cursor-pointer [&>option]:bg-[#0A0A0C] [&>option]:text-white`}
                value={form.service}
                onChange={update("service")}
              >
                <option value="">Select a service</option>
                {SERVICES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-[0.15em] text-[#5A6472] mb-2 block">
                How can we help? *
              </label>
              <textarea
                data-testid="contact-message"
                rows={4}
                className={`${inputClass} resize-none`}
                placeholder="Tell us about your current setup and what's slowing you down..."
                value={form.message}
                onChange={update("message")}
              />
            </div>
          </div>

          <button
            type="submit"
            data-testid="contact-submit"
            disabled={loading}
            className="group mt-10 inline-flex items-center gap-2 rounded-full bg-[#00E5FF] text-[#050505] px-8 py-4 font-semibold hover:bg-white transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" /> Sending…
              </>
            ) : (
              <>
                Send message
                <ArrowUpRight
                  size={18}
                  className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
};
